import { React, useState, useEffect, useContext } from "react";
import { MTable, MTag, Text, Flex } from "@cisco/spaces-dash-lib-styleguide";
import * as BusinessHierarchyService from "../../service/business-hierarchy-service";
import useLoader from "../../hooks/useLoader";
import { I18nContext } from "../../contexts/i18nContext";
import "../../common/common_style.scss";
import "../../common/global_style.scss";
import "./license-usage.scss";

function LicenseUsage(props) {
  const { setLoading } = useLoader();
  const { t } = useContext(I18nContext);
  const { locationId, locationName, isSmartLicenseEnabled, availableLicenses } = props;

  const [licenseList, setLicenseList] = useState([]);
  const [licenseTypesCount, setLicenseTypesCount] = useState(0);
  const [locLicenseObjLoaded, setLocLicenseObjLoaded] = useState(false);

  const isEmptyObject = (obj) => {
    if (obj !== undefined && obj !== null) {
      const isEmptyObj = obj && Object.keys(obj).length === 0;
      return isEmptyObj;
    }
    return true;
  };

  const getLicenseType = () => {
    setLicenseTypesCount(0);
    setLicenseList([]);
    setLocLicenseObjLoaded(false);
    const licenseCountList = [];
    setLoading(true);

    BusinessHierarchyService.getLicenseType(locationId)
      .then((response) => {
        setLoading(false);
        let licenseCount = 0;
        const licenseObj = response.data;

        if (licenseObj) {
          const licenses = availableLicenses;

          licenses.forEach((license) => {
            const { type, displayName, color } = license;
            const licenseData = licenseObj[type];

            if (licenseData && !isEmptyObject(licenseData)) {
              const licenseDetails = {
                ...licenseData,
                apCount: licenseData.apCount || 0,
                locCount: licenseData.locCount || 0,
                cameraCount: licenseData.cameraCount || 0,
                webexDeviceCount: licenseData.webexDeviceCount || 0,
                buildingUnitsCount: licenseData.buildingUnitsCount || 0,
                isEmptyObject: false,
                licenseType: displayName,
                tagColor: color.solid
              };

              licenseCount++;
              licenseCountList.push(licenseDetails);
            }
          });

          setLicenseTypesCount(licenseCount);
          setLicenseList([...licenseCountList]);
          setLocLicenseObjLoaded(true);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.log(`LicenseUsage: Error on getLicenseType ${error}`);
        setLicenseList([]);
        setLicenseTypesCount(0);
        setLocLicenseObjLoaded(false);
      });
  };

  const getLicenseGlobalUsage = () => {
    setLicenseTypesCount(0);
    setLicenseList([]);
    setLocLicenseObjLoaded(false);
    setLoading(true);

    BusinessHierarchyService.getLicenseGlobalUsage()
      .then((response) => {
        setLoading(false);
        const licenceUsageData = response.data;
        const dataArr = [];

        if (licenceUsageData) {
          const licenses = availableLicenses;

          Object.keys(licenceUsageData).forEach((key) => {
            const usageData = licenceUsageData[key];

            if (usageData && usageData.apCount !== undefined) {
              const licenseMatch = licenses.find((license) => license.type === key || license.variations.includes(key));

              if (licenseMatch) {
                const { displayName, color } = licenseMatch;
                dataArr.push({
                  licenseType: displayName,
                  tagColor: color.solid,
                  ...usageData,
                  licenseCount: licenceUsageData.licenseCount
                });
              } else {
                dataArr.push({
                  licenseType: key,
                  tagColor: "#6977F0",
                  ...usageData,
                  licenseCount: licenceUsageData.licenseCount
                });
              }
            }
          });
        }

        setLicenseList(dataArr);
        setLicenseTypesCount(dataArr.length);
        setLocLicenseObjLoaded(true);
      })
      .catch((error) => {
        setLoading(false);
        console.log(`LicenseUsage: Error on getLicenseGlobalUsage ${error}`);
        setLicenseList([]);
        setLicenseTypesCount(0);
        setLocLicenseObjLoaded(false);
      });
  };

  const getCellValueWithLicenseTag = ({ row }) => {
    return (
      <MTag.Colored
        style={{
          backgroundColor: row.original.tagColor ? row.original.tagColor : "#6977F0",
          color: "#fff"
        }}
        size="lg"
      >
        {row.original.licenseType ? row.original.licenseType.toUpperCase() : "—"}
      </MTag.Colored>
    );
  };

  useEffect(() => {
    console.log("LicenseUsage Mount");
    if (isSmartLicenseEnabled && locationId && locationName) {
      getLicenseType();
    } else {
      getLicenseGlobalUsage();
    }

    return () => {
      console.log("LicenseUsage Destroy");
    };
  }, [locationId, locationName]);

  return (
    <div className="business-hierarchy-license-wrappper">
      <div className="business-hierarchy-license-count-desc">
        <Flex gap={5}>
          <Text size="p3" weight="regular">
            {t("license-usage.you-have")}
          </Text>
          <Text size="p3" weight="semi-bold">
            {licenseTypesCount} {t("license-usage.license-space")}
          </Text>
          <Text size="p3" weight="regular">
            {t("license-usage.types-under")}
          </Text>
          <Text size="p3" weight="semi-bold">
            {locationName}
          </Text>
        </Flex>
      </div>
      {licenseList && licenseList.length > 0 && (
        <div className="userTableContainer">
          <MTable.Pagination key={licenseList.length} currentPage={1} defaultRecordsOption="10" showNavigation="all" showTotal showOptions>
            <MTable
              columns={[
                {
                  accessorKey: "licenseType",
                  header: t("license-usage.license-header"),
                  cell: getCellValueWithLicenseTag,
                  meta: {
                    align: "left"
                  }
                },
                {
                  accessorKey: "apCount",
                  header: t("license-usage.accesspoints-header"),
                  meta: {
                    align: "left"
                  }
                },
                {
                  accessorKey: "webexDeviceCount",
                  header: t("license-usage.webex-device-header"),
                  meta: {
                    align: "left"
                  }
                },
                {
                  accessorKey: "cameraCount",
                  header: t("license-usage.meraki-camera-header"),
                  meta: {
                    align: "left"
                  }
                },
                {
                  accessorKey: "locCount",
                  header: t("license-usage.locations-header"),
                  meta: {
                    align: "left"
                  }
                },
                {
                  accessorKey: "buildingUnitsCount",
                  header: t("license-usage.building-units-header"),
                  meta: {
                    align: "left"
                  },
                  cell: (data) => <Text>{data.getValue() !== undefined ? data.renderValue() : "—"}</Text>
                }
              ]}
              enablePagination={false}
              data={licenseList}
              isLoading={!locLicenseObjLoaded}
            />
          </MTable.Pagination>
        </div>
      )}
    </div>
  );
}

export default LicenseUsage;
